import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ComponentFactoryResolver,
  ContentChildren,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, filter, map, take, takeUntil, tap } from 'rxjs/operators';

import { clearTimeout, setTimeout } from '@rx-angular/cdk/zone-less/browser';
import { SortItemsType } from '@shared/interfaces/list-fields.interface';
import { ItemRowComponent } from '@ui-kit/list/item-row/item-row.component';
import { ListComponent } from '@ui-kit/list/list.component';

@Component({
  selector: 'list-v2',
  templateUrl: './list-v2.component.html',
  styleUrls: ['./list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ListV2Component<T> extends ListComponent<T> implements OnInit, AfterViewInit, AfterContentInit, OnDestroy {
  private _renderer = inject(Renderer2);
  private _element = inject(ElementRef);
  private _resolver = inject(ComponentFactoryResolver);
  private _scrollListener: () => void;

  @Input() fieldsChanged: Subject<Array<SortItemsType>>;
  @Input() sumMinWidth = 0;
  @Input() stickyOptions: boolean = false;
  @Input() numberFlexColumns = 0;
  @Input() parentWidth: number;
  @Input() fetchMethod: string;

  @ViewChild('rowContainer', { read: ViewContainerRef, static: false }) rowContainer;
  @ViewChild('heightContainer') heightContainer: ElementRef;
  @ViewChild('scrollContainer') scrollContainer: ElementRef;
  @ViewChild('noSet', { read: ViewContainerRef, static: false }) noSet;

  timeouts = [];
  fieldsWidthChanged = new Subject();
  componentRef = null;

  @ContentChildren(TemplateRef, { read: TemplateRef }) fieldTemplates: QueryList<any>;

  totalContentHeight = '0px';
  offsetY = 0;

  visibleChildren$ = new Subject();

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this._cdRef.markForCheck();
  }

  get element(): HTMLElement {
    return this._element.nativeElement;
  }

  clickOnItem(e, field, item): void {
    e.preventDefault();
    e.stopPropagation();

    if (Object.prototype.hasOwnProperty.call(field, 'clickable') && !field.clickable) {
      return;
    }

    if (this.submitClick.observers.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      this.submitClick.next(item);
    }
  }

  filtersChanged(filter): void {
    this.collection.setParams('page', 1);
    filter ? this.collection.setParams('sort', filter) : this.collection.clearParams('sort');
    this.collection.search(this.fetchMethod ?? null); 
  }

  ngOnInit(): void {
    this.fieldsChangeSubscription();

    this.collection.onLoad$
      .pipe(
        take(1),
        filter(() => this.collection.loadByScrolling),
        tap(() => {
          const total = this.collection.items.length;
          this.runInfinite(total);
        })
      )
      .subscribe();

    this.collection.onLoad$
      .pipe(
        takeUntil(this._destroyed),
        filter(() => !this.collection.loadByScrolling),
        debounceTime(160),
        tap(() => {
          const innerHeight = this.heightContainer.nativeElement.firstChild.offsetHeight;
          this.totalContentHeight = innerHeight + 'px';
          this._cdRef.markForCheck();
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    while (this.timeouts.length) {
      clearTimeout(this.timeouts.pop());
    }

    if (this._scrollListener) {
      this._scrollListener();
    }
    this._destroyed.next(true);
    this._destroyed.complete();
  }

  ngAfterContentInit(): void {
    super.ngAfterContentInit();

    const t = setTimeout(() => {
      let perPage = this.collection.getParams('per_page');
      if (perPage === -1) {
        perPage = 30;
      }
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const loadByScrolling = this.collection.loadByScrolling;
      if (loadByScrolling && perPage > 15) {
        perPage = 30;
      }

      if (perPage === -1) {
        perPage = 30;
      }

      for (let i = 0; i < perPage; i++) {
        this.createListRowComponent({
          index: i
        });
      }
      this._cdRef.markForCheck();
    }, 100);

    this.timeouts.push(t);
  }

  createListRowComponent({ index }): void {
    const factory = this._resolver.resolveComponentFactory(ItemRowComponent);
    this.componentRef = this.rowContainer.createComponent(factory);
    this.componentRef.instance.fields = this.fields;
    this.componentRef.instance.index = index;
    this.componentRef.instance.index$ = this.visibleChildren$;
    this.componentRef.instance.templates = this.templates;
    this.componentRef.instance.item = this.collection.items[index];
    this.componentRef.instance.noSet = this.noSet;
    this.componentRef.instance.fieldsChanged = this.fieldsWidthChanged;
    this.componentRef.instance.itemHeaderTemplate = this.itemHeaderTemplate;
    this.componentRef.instance.itemFooterTemplate = this.itemFooterTemplate;
    this.componentRef.instance.collection = this.collection;

    this.componentRef.instance.submitClick
      .pipe(
        takeUntil(this._destroyed),
        map(({ event, item, field }) => this.clickOnItem(event, field, item))
      )
      .subscribe();
  }

  onScroll(): void {
    if (!this.collection.loadByScrolling) {
      return;
    }
    if (this.collection.meta.currentPage === this.collection.meta.pageCount) {
      return;
    }

    if (!this.collection.loading && this.collection.loaded && this.collection.items.length) {
      const page = this.collection.meta.currentPage + 1;
      this.collection.setParams('page', page).search(this.fetchMethod ?? null)
    }
  }

  virtualScroll({ itemCount, viewportHeight, rowHeight, nodePadding, scrollTop }): void {
    this.totalContentHeight = itemCount * rowHeight + 'px';
    let startNode = Math.floor(scrollTop / rowHeight) - nodePadding;
    startNode = Math.max(0, startNode);

    let visibleNodesCount = Math.ceil(viewportHeight / rowHeight) + 2 * nodePadding;
    visibleNodesCount = Math.min(itemCount - startNode, visibleNodesCount);

    visibleNodesCount = 30;
    const start = startNode;
    this.offsetY = start * rowHeight;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const visibleChildren = new Array(visibleNodesCount).fill(null).map((_, index) => index + startNode);
    this.visibleChildren$.next(visibleChildren);
  }

  runInfinite(totalCount = 100): void {
    const container = this.scrollContainer.nativeElement;
    const rowHeight = 50;
    const nodePadding = 2;

    const options = {
      itemCount: totalCount,
      viewportHeight: container.offsetHeight,
      rowHeight,
      nodePadding,
      scrollTop: container.scrollTop
    };

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const renderScroll = (options) => {
      console.log('renderScroll', options.itemCount);
      this.virtualScroll(options);
    };

    renderScroll(options);

    let animationFrame;

    this._renderer.listen(this._element.nativeElement, 'scroll', (e) => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
      animationFrame = requestAnimationFrame(() => {
        // @ts-ignore
        options.scrollTop = e.target.scrollTop;
        renderScroll(options);
      });
    });

    this.collection.onLoad$
      .pipe(
        takeUntil(this._destroyed),
        filter(() => this.collection.loadByScrolling),
        map(() => {
          options.itemCount = this.collection.items.length;
          if (this.collection.getParams('page') === 1) {
            setTimeout(() => {
              const target = this._element.nativeElement;
              target.scrollTo({ top: 0, behavior: 'smooth' });
            }, 100);
          }
          renderScroll(options);
        })
      )
      .subscribe();
  }

  private fieldsChangeSubscription(): void {
    if (this.fieldsChanged) {
      this.fieldsChanged
        .pipe(
          takeUntil(this._destroyed),
          filter((fields) => fields instanceof Array),
          map((fields: [any]) => {
            this.fields = fields;
            this.fields.forEach((item) => {
              if (this.templates && this.templates[item.key + 'Field']) {
                item['html'] = this.templates[item.key + 'Field'];
              }
            });

            return this.fieldsWidthChanged.next(fields);
          })
        )
        .subscribe();
    }
  }
}
